import type { BooleanResolver } from '@pct-ts/typing';
import type { MimeTypes } from '@pct-ng/common/file';
import { isObject } from '@pct-ts/typeguard';

interface BaseFileUploadConfig {
  accept?: MimeTypes | MimeTypes[];
  /**
   * default: false
   */
  multiple: boolean;
  /**
   * in bytes
   * default: infinity
   */
  maxSize?: number;
  /**
   * default false
   */
  removableRow?: BooleanResolver<File>;
  /**
   * default true
   */
  showPreview?: BooleanResolver<File>;
  /**
   * When there are already selected files and we open the file selection and select new files,
   * should we overwrite the current value
   *
   * default true
   */
  overwriteNewUpload?: boolean;

  cropConfig?: CropConfig;

  multipleUpload: boolean;
}

export interface SingleFileUploadConfig extends BaseFileUploadConfig {
  multiple: false;

  multipleUpload: false;
}

export interface MultipleFileUploadConfig extends BaseFileUploadConfig {
  multiple: true;
  /**
   * default: infinity
   */
  maxFiles: number;
}

export type AspectRatio = '4:3' | '16:9';

export type CropConfig = {
  showAspectRatio: boolean;
  defaultAspectRatio: AspectRatio;
  showContainWithinAspectRatio: boolean;
  containWithinAspectRatioDefaultValue: boolean;
  showMaintainAspectRatio: boolean;
  maintainAspectRatioDefaultValue: boolean;
  showRotate: boolean;
};

export function aspectRatioToNum(aspectRatio: AspectRatio): number {
  switch (aspectRatio) {
    case '4:3':
      return 4 / 3;
    case '16:9':
      return 16 / 9;
  }
}

export type FileUploadConfig = SingleFileUploadConfig | MultipleFileUploadConfig;

export function isSingleFileUploadConfig(value: unknown): value is SingleFileUploadConfig {
  return isObject(value) && value !== null && 'multiple' in value && value.multiple === false;
}

export function isMultipleFileUploadConfig(value: unknown): value is MultipleFileUploadConfig {
  return isObject(value) && value !== null && 'multiple' in value && value.multiple === true;
}
